export type ProfileTab = 'resumen' | 'productos' | 'calificaciones' | 'proyectos' | 'cotizaciones' | 'documentaciones' | 'accesos';

export const TAB_TO_QUERY_PARAM: Record<string, ProfileTab> = {
  'Resumen': 'resumen',
  'Productos/Servicios': 'productos',
  'Calificaciones': 'calificaciones',
  'Proyectos publicados': 'proyectos',
  'Cotización de materiales': 'cotizaciones',
  'Documentaciones de obra': 'documentaciones',
  'Accesos a obras': 'accesos',
};

export const QUERY_PARAM_TO_TAB: Record<ProfileTab, string> = {
  resumen: 'Resumen',
  productos: 'Productos/Servicios',
  calificaciones: 'Calificaciones',
  proyectos: 'Proyectos publicados',
  cotizaciones: 'Cotización de materiales',
  documentaciones: 'Documentaciones de obra',
  accesos: 'Accesos a obras',
};

const resolveProfileTab = (input?: string | null): ProfileTab | null => {
  if (!input) return null;
  const value = input.trim();
  const direct = TAB_TO_QUERY_PARAM[value];
  if (direct) return direct;
  if (Object.prototype.hasOwnProperty.call(QUERY_PARAM_TO_TAB, value)) {
    return value as ProfileTab;
  }
  const lower = value.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(QUERY_PARAM_TO_TAB, lower)) {
    return lower as ProfileTab;
  }
  return null;
};

export const PROFILE_SUBITEM_TAB_MAPPING: Record<string, string> = {
  'mis-proyectos': 'Proyectos publicados',
  'mis-cotizaciones-materiales': 'Cotización de materiales',
  'mis-documentaciones': 'Documentaciones de obra',
  'mis-productos-servicios': 'Productos/Servicios',
  'mis-postulaciones': 'Accesos a obras',
};

export const PROFILE_TAB_TO_NAV_ITEM: Record<string, string> = {
  'Proyectos publicados': 'mis-contenidos',
  'Cotización de materiales': 'mis-contenidos',
  'Documentaciones de obra': 'mis-contenidos',
  'Productos/Servicios': 'mis-contenidos',
  'Accesos a obras': 'mis-contenidos',
};

export const getUserDisplayName = (user: any): string | null => {
  if (!user) return null;
  if (user.type === "COMPANY") {
    return user.razon_social || user.business_name || null;
  }
  const nameParts = [user.name, user.last_name].filter(Boolean);
  if (!nameParts.length) return null;
  const displayName = nameParts.join(" ").trim();
  return displayName || null;
};

export interface ProfilePathInfo {
  isProfile: boolean;
  userId: string | null;
  userName: string | null;
}

export const getProfilePathInfo = (pathname: string): ProfilePathInfo => {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  const isProfileRoute = first === "profesionales" || first === "perfil";
  if (!isProfileRoute) {
    return {
      isProfile: false,
      userId: null,
      userName: null,
    };
  }

  const userId = segments[1] || null;
  const rawName = segments.length > 2 ? segments.slice(2).join("/") : null;
  const userName = rawName ? decodeURIComponent(rawName) : null;

  return {
    isProfile: Boolean(userId),
    userId,
    userName,
  };
};

export interface NavigationFromProfile {
  from: 'perfil';
  tab: ProfileTab;
  userId: string;
  userName: string;
}

export const buildProfileUrl = (userId: string, userName?: string | null, tab?: string | null): string => {
  const baseUrl = `/perfil/${userId}`;
  const search = new URLSearchParams();
  const resolvedTab = resolveProfileTab(tab);
  if (resolvedTab) {
    search.set("tab", resolvedTab);
  }
  if (userName) {
    search.set("userName", userName);
  }
  const query = search.toString();
  return query ? `${baseUrl}?${query}` : baseUrl;
};

export const buildNavigationParams = (tab: string, userId: string, userName: string): string => {
  const search = new URLSearchParams();
  search.set("from", "perfil");
  const resolvedTab = resolveProfileTab(tab) || tab;
  search.set("tab", resolvedTab);
  search.set("userId", userId);
  search.set("userName", userName);
  const query = search.toString();
  return query ? `?${query}` : "";
};

export const parseNavigationParams = (searchParams: URLSearchParams): NavigationFromProfile | null => {
  const from = searchParams.get('from');
  const rawTab = searchParams.get('tab');
  const tab = resolveProfileTab(rawTab);
  const userId = searchParams.get('userId');
  const userName = searchParams.get('userName');

  if (from === 'perfil' && tab && userId && userName) {
    let resolvedName = userName;
    try {
      resolvedName = decodeURIComponent(userName);
    } catch {
      resolvedName = userName;
    }
    return {
      from: 'perfil',
      tab,
      userId,
      userName: resolvedName,
    };
  }

  return null;
};

export const getProfileTabFromQuery = (searchParams: URLSearchParams): string | null => {
  const rawTab = searchParams.get('tab');
  const tabParam = resolveProfileTab(rawTab);
  if (tabParam && QUERY_PARAM_TO_TAB[tabParam]) {
    return QUERY_PARAM_TO_TAB[tabParam];
  }
  return null;
};

export interface ProfessionalsListNavigationParams {
  from: 'profesionales';
  skillId?: number;
  jurisdictionIds: number[];
  allCountry: boolean;
  page?: number;
}

export const parseProfessionalsListNavigationParams = (
  searchParams: URLSearchParams
): ProfessionalsListNavigationParams | null => {
  const from = searchParams.get('from');
  if (from !== 'profesionales') {
    return null;
  }

  const skillParam = searchParams.get('skill');
  const jurisdictionsParam = searchParams.get('jurisdictions');
  const allCountryParam = searchParams.get('allCountry');
  const pageParam = searchParams.get('page');

  const skillId = skillParam ? Number(skillParam) : undefined;
  const jurisdictionIds = jurisdictionsParam
    ? jurisdictionsParam
        .split(',')
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value))
    : [];
  const allCountry = allCountryParam === '1';
  const page = pageParam ? Number(pageParam) : undefined;

  return {
    from: 'profesionales',
    skillId,
    jurisdictionIds,
    allCountry,
    page,
  };
};

export const buildProfessionalsListUrl = (params: {
  skillId?: number;
  jurisdictionIds?: number[];
  allCountry?: boolean;
  page?: number;
}) => {
  const search = new URLSearchParams();
  if (typeof params.skillId === 'number') {
    search.set('skill', String(params.skillId));
  }
  if (params.jurisdictionIds && params.jurisdictionIds.length > 0) {
    search.set('jurisdictions', params.jurisdictionIds.join(','));
  }
  if (params.allCountry) {
    search.set('allCountry', '1');
  }
  if (params.page && params.page > 1) {
    search.set('page', String(params.page));
  }
  const queryString = search.toString();
  return `/buscar-perfiles${queryString ? `?${queryString}` : ''}`;
};

export const getProfessionalsFiltersFromSearch = (searchParams: URLSearchParams) => {
  const skillParam = searchParams.get('skill');
  const jurisdictionsParam = searchParams.get('jurisdictions');
  const allCountryParam = searchParams.get('allCountry');
  const pageParam = searchParams.get('page');

  if (!skillParam && !jurisdictionsParam && !allCountryParam && !pageParam) {
    return null;
  }

  const skillId = skillParam ? Number(skillParam) : undefined;
  const jurisdictionIds = jurisdictionsParam
    ? jurisdictionsParam
        .split(',')
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value))
    : [];
  const allCountry = allCountryParam === '1';
  const page = pageParam ? Number(pageParam) : undefined;

  return {
    skillId,
    jurisdictionIds,
    allCountry,
    page,
  };
};